/**
 * Model Configuration Page
 * Manages AI models via Seed gRPC server
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CloudDownload as CloudIcon,
} from '@mui/icons-material';
import {
  getModelConfigs,
  updateModelConfigs,
  addModelFromS3,
  removeModel,
  TaskTypeEnum,
  getTaskTypeName,
} from '../services/seedGrpcService.ts';

interface ModelConfig {
  modelName: string;
  modelPath: string;
  taskType: number;
  jsonConfig: string;
  interSup: boolean;
  tileWidth: number;
  tileHeight: number;
}

const ModelConfiguration: React.FC = () => {
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Selected model for edit/delete
  const [selectedModel, setSelectedModel] = useState<ModelConfig | null>(null);
  const [selectedModelIndex, setSelectedModelIndex] = useState<number>(-1);

  // Form data for add/edit
  const [formData, setFormData] = useState<ModelConfig>({
    modelName: '',
    modelPath: '',
    taskType: TaskTypeEnum.SEMANTIC_SEGMENTATION,
    jsonConfig: '',
    interSup: true,
    tileWidth: 1246,
    tileHeight: 924,
  });

  // S3 form data for adding model
  const [s3FormData, setS3FormData] = useState({
    modelName: '',
    modelS3Uri: '',
    jsonConfigS3Uri: '',
    taskType: TaskTypeEnum.SEMANTIC_SEGMENTATION,
    interSup: true,
    tileWidth: 1246,
    tileHeight: 924,
  });

  // Load models on mount
  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getModelConfigs();
      setModels(response.configsList || []);
      console.log('[ModelConfiguration] Loaded models:', response.configsList);
    } catch (err: any) {
      console.error('[ModelConfiguration] Error loading models:', err);
      setError(err.message || 'Failed to load models');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (model: ModelConfig, index: number) => {
    setSelectedModel(model);
    setSelectedModelIndex(index);
    setFormData({ ...model });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      // Update the model in the list
      const updatedModels = [...models];
      updatedModels[selectedModelIndex] = formData;

      // Send update to server
      const response = await updateModelConfigs(updatedModels);

      if (response.success) {
        setSuccess(`Model '${formData.modelName}' updated successfully`);
        setModels(response.currentConfigs.configsList || []);
        setEditDialogOpen(false);
      } else {
        setError(response.message || 'Update failed');
      }
    } catch (err: any) {
      console.error('[ModelConfiguration] Error updating model:', err);
      setError(err.message || 'Failed to update model');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setS3FormData({
      modelName: '',
      modelS3Uri: '',
      jsonConfigS3Uri: '',
      taskType: TaskTypeEnum.SEMANTIC_SEGMENTATION,
      interSup: true,
      tileWidth: 1246,
      tileHeight: 924,
    });
    setAddDialogOpen(true);
  };

  const handleAddSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await addModelFromS3(s3FormData);

      if (response.success) {
        setSuccess(`Model '${s3FormData.modelName}' added successfully from S3`);
        setModels(response.currentConfigs.configsList || []);
        setAddDialogOpen(false);
      } else {
        setError(response.message || 'Add failed');
      }
    } catch (err: any) {
      console.error('[ModelConfiguration] Error adding model:', err);
      setError(err.message || 'Failed to add model from S3');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (model: ModelConfig) => {
    setSelectedModel(model);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedModel) return;

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await removeModel(selectedModel.modelName);

      if (response.success) {
        setSuccess(`Model '${selectedModel.modelName}' removed successfully`);
        setModels(response.currentConfigs.configsList || []);
        setDeleteDialogOpen(false);
      } else {
        setError(response.message || 'Delete failed');
      }
    } catch (err: any) {
      console.error('[ModelConfiguration] Error deleting model:', err);
      setError(err.message || 'Failed to remove model');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
              Model Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage AI models for inference tasks
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadModels}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              disabled={loading}
            >
              Add Model from S3
            </Button>
          </Box>
        </Box>

        {/* Status Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
      </Paper>

      {/* Models Table */}
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'rgba(26, 188, 156, 0.08)' }}>
              <TableCell><strong>Model Name</strong></TableCell>
              <TableCell><strong>Task Type</strong></TableCell>
              <TableCell><strong>Model Path</strong></TableCell>
              <TableCell><strong>Tile Size</strong></TableCell>
              <TableCell><strong>Inter Sup</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && models.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : models.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No models configured. Click "Add Model from S3" to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              models.map((model, index) => (
                <TableRow key={model.modelName} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {model.modelName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getTaskTypeName(model.taskType)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={model.modelPath}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                        {model.modelPath}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {model.tileWidth} × {model.tileHeight}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={model.interSup ? 'Yes' : 'No'}
                      size="small"
                      color={model.interSup ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit Model">
                      <IconButton
                        color="primary"
                        onClick={() => handleEditClick(model, index)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Model">
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(model)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Model Configuration</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Model Name"
              value={formData.modelName}
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <TextField
              label="Model Path"
              value={formData.modelPath}
              onChange={(e) => setFormData({ ...formData, modelPath: e.target.value })}
              fullWidth
            />
            <TextField
              label="JSON Config Path"
              value={formData.jsonConfig}
              onChange={(e) => setFormData({ ...formData, jsonConfig: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Task Type</InputLabel>
              <Select
                value={formData.taskType}
                onChange={(e) => setFormData({ ...formData, taskType: Number(e.target.value) })}
                label="Task Type"
              >
                <MenuItem value={TaskTypeEnum.SEMANTIC_SEGMENTATION}>Semantic Segmentation</MenuItem>
                <MenuItem value={TaskTypeEnum.IMAGE_REGENERATION}>Image Regeneration</MenuItem>
                <MenuItem value={TaskTypeEnum.OBJECT_DETECTION}>Object Detection</MenuItem>
                <MenuItem value={TaskTypeEnum.DAMAGE_CLASSIFICATION}>Damage Classification</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Tile Width"
                type="number"
                value={formData.tileWidth}
                onChange={(e) => setFormData({ ...formData, tileWidth: Number(e.target.value) })}
                fullWidth
              />
              <TextField
                label="Tile Height"
                type="number"
                value={formData.tileHeight}
                onChange={(e) => setFormData({ ...formData, tileHeight: Number(e.target.value) })}
                fullWidth
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.interSup}
                  onChange={(e) => setFormData({ ...formData, interSup: e.target.checked })}
                />
              }
              label="Inter Supervision"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Model Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudIcon color="primary" />
            Add Model from S3
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Model Name"
              value={s3FormData.modelName}
              onChange={(e) => setS3FormData({ ...s3FormData, modelName: e.target.value })}
              placeholder="e.g., NEW-MODEL"
              required
              fullWidth
            />
            <TextField
              label="Model S3 URI"
              value={s3FormData.modelS3Uri}
              onChange={(e) => setS3FormData({ ...s3FormData, modelS3Uri: e.target.value })}
              placeholder="s3://bucket-name/path/to/model.pth"
              required
              fullWidth
            />
            <TextField
              label="JSON Config S3 URI"
              value={s3FormData.jsonConfigS3Uri}
              onChange={(e) => setS3FormData({ ...s3FormData, jsonConfigS3Uri: e.target.value })}
              placeholder="s3://bucket-name/path/to/config.json"
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Task Type</InputLabel>
              <Select
                value={s3FormData.taskType}
                onChange={(e) => setS3FormData({ ...s3FormData, taskType: Number(e.target.value) })}
                label="Task Type"
              >
                <MenuItem value={TaskTypeEnum.SEMANTIC_SEGMENTATION}>Semantic Segmentation</MenuItem>
                <MenuItem value={TaskTypeEnum.IMAGE_REGENERATION}>Image Regeneration</MenuItem>
                <MenuItem value={TaskTypeEnum.OBJECT_DETECTION}>Object Detection</MenuItem>
                <MenuItem value={TaskTypeEnum.DAMAGE_CLASSIFICATION}>Damage Classification</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Tile Width"
                type="number"
                value={s3FormData.tileWidth}
                onChange={(e) => setS3FormData({ ...s3FormData, tileWidth: Number(e.target.value) })}
                fullWidth
              />
              <TextField
                label="Tile Height"
                type="number"
                value={s3FormData.tileHeight}
                onChange={(e) => setS3FormData({ ...s3FormData, tileHeight: Number(e.target.value) })}
                fullWidth
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={s3FormData.interSup}
                  onChange={(e) => setS3FormData({ ...s3FormData, interSup: e.target.checked })}
                />
              }
              label="Inter Supervision"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddSave}
            variant="contained"
            disabled={loading || !s3FormData.modelName || !s3FormData.modelS3Uri || !s3FormData.jsonConfigS3Uri}
          >
            {loading ? <CircularProgress size={24} /> : 'Add Model'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove the model <strong>{selectedModel?.modelName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModelConfiguration;
